// src/pages/Epoch.jsx
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Plot from 'react-plotly.js';
import { NavigationContext } from '../context/NavigationContext';
import { ChatContext } from '../context/ChatContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import useWindowResize from '../hooks/useWindowResize';
import DemoWalkthrough, { DontShowLink } from '../components/DemoWalkthrough';
import { shouldResume } from '../utils/walkthroughState';
import API_URL from '../config';

const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-full">
    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
  </div>
);

const Epoch = () => {
  const { recordingId, epochId } = useParams();
  const navigate = useNavigate();
  const { updateLevel, openLevel } = useContext(NavigationContext);
  const { setChatDictionary } = useContext(ChatContext);
  const windowSize = useWindowResize();

  // Data states.
  const [epochData, setEpochData] = useState(null);
  const [epochsList, setEpochsList] = useState([]);
  const [selectedStages, setSelectedStages] = useState({});
  
  // Loading and error states.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Parse the epoch id (database id) from URL.
  const currentEpoch = parseInt(epochId, 10);
  // Sampling frequency.
  const sf = 50;
  
  // --- Effects: Data Fetching & Banner Update ---
  useEffect(() => {
    updateLevel('epoch', { 
      label: `Epoch ${epochId}`, 
      route: `/analysis/${recordingId}/epoch/${epochId}` 
    });
  }, [epochId, updateLevel]);

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        const currentIndex = epochsList.findIndex(e => e.id === currentEpoch);
        if (currentIndex > 0) {
          navigate(`/analysis/${recordingId}/epoch/${epochsList[currentIndex - 1].id}`);
        }
      } else if (event.key === 'ArrowRight') {
        const currentIndex = epochsList.findIndex(e => e.id === currentEpoch);
        if (currentIndex < epochsList.length - 1) {
          navigate(`/analysis/${recordingId}/epoch/${epochsList[currentIndex + 1].id}`);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [epochsList, currentEpoch, navigate]);

  // Combined data fetching
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [epochResponse, epochsListResponse] = await Promise.all([
          fetch(`${API_URL}/api/epochs/${epochId}/`),
          fetch(`${API_URL}/api/epochs/?recording_id=${recordingId}`)
        ]);

        if (!epochResponse.ok || !epochsListResponse.ok) {
          throw new Error('Failed to fetch data');
        }

        const epochData = await epochResponse.json();
        const epochsListData = await epochsListResponse.json();

        setEpochData(epochData);
        setEpochsList(epochsListData);

        // Set selected stage if there's a manual stage
        const manualStage = epochData.sleep_stages.find(ss => ss.staging_method === 'manual');
        if (manualStage) {
          setSelectedStages(prev => ({
            ...prev,
            [epochId]: manualStage.sleep_stage
          }));
        }
      } catch (err) {
        console.error("Error fetching data:", err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [epochId]);

  // --- useMemo Hooks (always executed) ---

  const patternBased = useMemo(() => 
    epochData?.sleep_stages.find(ss => ss.staging_method === "YASA"),
    [epochData]
  );
  const ruleBased = useMemo(() => 
    epochData?.sleep_stages.find(ss => ss.staging_method === "rule-based"),
    [epochData]
  );
  const manualStage = useMemo(() => 
    epochData?.sleep_stages.find(ss => ss.staging_method === "manual"),
    [epochData]
  );
  
  const stageMapping = { 'Wake': 4, 'REM': 3, 'N1': 2, 'N2': 1, 'N3': 0 };

  const xEpochs = useMemo(() => epochsList.map(e => e.epoch_number), [epochsList]);
  const yEpochs = useMemo(() => 
    epochsList.map(e => stageMapping[e.sleep_stage] !== undefined ? stageMapping[e.sleep_stage] : null),
    [epochsList]
  );
  
  const currentEpochObject = useMemo(() => 
    epochsList.find(e => e.id === currentEpoch),
    [currentEpoch, epochsList]
  );
  const currentEpochNumber = useMemo(() => 
    currentEpochObject ? currentEpochObject.epoch_number : null,
    [currentEpochObject]
  );
  
  const verticalLine = useMemo(() => ({
    type: 'line',
    x0: currentEpochNumber,
    x1: currentEpochNumber,
    y0: 0,
    y1: 1,
    xref: 'x',
    yref: 'paper',
    line: { color: 'blue', width: 2 }
  }), [currentEpochNumber]);
  
  // --- Update Chat Context ---
  useEffect(() => {
    if (epochData) {
      const sleepStage = (patternBased && ruleBased && patternBased.sleep_stage === ruleBased.sleep_stage)
        ? patternBased.sleep_stage
        : 'undefined';
      const chatDictionary = {
        level: 'epoch',
        sleepStage: sleepStage,
        ruleBased: ruleBased,
        patternBased: patternBased,
        annotations: epochData.annotations,
      };
      setChatDictionary(chatDictionary);
    }
  }, [epochId, patternBased, ruleBased, setChatDictionary, epochData]);
  
  // --- Function Definitions (declared unconditionally) ---

  const renderEpochSignalPlot = (signal, index) => {
    // Parse the data array
    let dataArray = signal.signal_data;
    if (typeof dataArray === 'string') {
      try {
        dataArray = JSON.parse(dataArray);
      } catch (err) {
        console.error("Error parsing signal data", err);
        dataArray = [];
      }
    }

    // Calculate y-axis range
    const isEEG = /^[FCO]/.test(signal.signal_name);
    const yRange = isEEG ? [-50, 50] : (() => {
      const minVal = Math.min(...dataArray);
      const maxVal = Math.max(...dataArray);
      const range = maxVal - minVal;
      const padding = range * 0.1;
      return [minVal - padding, maxVal + padding];
    })();

    // Get channel-specific annotations only
    const channelAnnotations = epochData?.annotations?.by_channel?.[signal.signal_name] || [];

    // Create shapes and annotations for this channel
    const channelShapes = channelAnnotations.map((ann) => ({
      type: 'rect',
      x0: ann.start * sf,
      x1: ann.end * sf,
      y0: 0,
      y1: 1,
      xref: 'x',
      yref: 'paper',
      fillcolor: 'rgba(211,211,211,0.3)',
      line: { width: 0 },
      // Add click event data
      customdata: {
        type: ann.annotation_type,
        start: ann.start,
        end: ann.end,
        channel: signal.signal_name
      }
    }));

    // Add horizontal reference lines for EEG channels
    if (/^[FCO]/.test(signal.signal_name)) {
      channelShapes.push(
        {
          type: 'line',
          x0: 0,
          x1: dataArray.length,
          y0: 38,
          y1: 38,
          line: {
            color: 'rgba(255, 0, 0, 0.3)',
            width: 1,
            dash: 'dot'
          }
        },
        {
          type: 'line',
          x0: 0,
          x1: dataArray.length,
          y0: -38,
          y1: -38,
          line: {
            color: 'rgba(255, 0, 0, 0.3)',
            width: 1,
            dash: 'dot'
          }
        }
      );
    }

    const channelPlotAnnotations = channelAnnotations.map((ann) => ({
      x: ann.end * sf,
      y: 0,
      xref: 'x',
      yref: 'paper',
      text: ann.annotation_type.toLowerCase(),
      showarrow: false,
      font: { size: 10, color: 'gray' },
      xanchor: 'right',
      yanchor: 'bottom',
      // Add click event data
      customdata: {
        type: ann.annotation_type,
        start: ann.start,
        end: ann.end,
        channel: signal.signal_name
      }
    }));

    return (
      <div
        key={index}
        className="flex items-center mb-1 border border-gray-300 bg-gray-50 transition-all duration-200"
        style={{ height: '70px' }}
      >
        <div className="w-24 p-2 text-sm font-bold text-gray-700">
          {signal.signal_name}
        </div>
        <div className="flex-1 h-full">
          <Plot
            data={[
              {
                x: dataArray.map((_, i) => i / sf),
                y: dataArray,
                mode: 'lines',
                type: 'scatter',
                line: { color: 'rgb(73,68,68)', width: 0.5 },
              },
            ]}
            layout={{
              margin: { l: 10, r: 10, t: 2, b: index === epochData.signals.length - 1 ? 30 : 2 },
              xaxis: { 
                showticklabels: index === epochData.signals.length - 1,
                range: [0, dataArray.length / sf],
                fixedrange: true,
                title: index === epochData.signals.length - 1 ? 'Time (s)' : null,
                dtick: 5,
                tickformat: 'd'
              },
              yaxis: { 
                showticklabels: false,
                fixedrange: true,
                range: yRange
              },
              shapes: channelShapes.map(shape => ({
                ...shape,
                x0: shape.x0 / sf,
                x1: shape.x1 / sf
              })),
              annotations: channelPlotAnnotations.map(ann => ({
                ...ann,
                x: ann.x / sf
              })),
              xaxis2: {
                matches: 'x',
                showticklabels: false,
                range: [0, dataArray.length / sf],
                fixedrange: true
              }
            }}
            config={{ 
              displayModeBar: false, 
              responsive: true,
              staticPlot: false
            }}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler={true}
            key={`signal-${index}-${windowSize.width}-${windowSize.height}`}
            onClick={(event) => {
              if (event.points && event.points[0]) {
                const clickX = event.points[0].x;
                
                // Find if we clicked on a shape or near an annotation
                const clickedShape = channelShapes.find(s => 
                  clickX >= s.x0 && clickX <= s.x1
                );
                
                const clickedAnnotation = channelPlotAnnotations.find(a => 
                  Math.abs(clickX - a.x) < 50  // Allow some pixels of tolerance for clicking annotations
                );

                const clickedElement = clickedShape || clickedAnnotation;
                
                if (clickedElement && clickedElement.customdata) {
                  const { type, start, end, channel } = clickedElement.customdata;
                  openLevel({
                    key: 'micro-annotation',
                    label: `${type} Annotation`,
                    route: `/analysis/${recordingId}/epoch/${epochId}/annotation/${type}/${start}/${end}/${channel}`
                  });
                }
              }
            }}
          />
        </div>
      </div>
    );
  };

  const handlePrevious = () => {
    const currentIndex = epochsList.findIndex(e => e.id === currentEpoch);
    if (currentIndex > 0) {
      navigate(`/analysis/${recordingId}/epoch/${epochsList[currentIndex - 1].id}`);
    }
  };

  const handleNext = () => {
    const currentIndex = epochsList.findIndex(e => e.id === currentEpoch);
    if (currentIndex < epochsList.length - 1) {
      navigate(`/analysis/${recordingId}/epoch/${epochsList[currentIndex + 1].id}`);
    }
  };

  const getButtonStyle = (stage) => {
    // If there's a manual stage and it matches the epoch's stage
    if (manualStage && manualStage.sleep_stage === epochData?.epoch?.sleep_stage) {
      return stage === manualStage.sleep_stage
        ? 'bg-gray-800 text-white'  // Black for manual stage
        : 'bg-gray-200 text-gray-800 hover:bg-gray-300';
    }
    
    // If there's no manual stage, use the epoch's stage
    if (epochData?.epoch?.sleep_stage) {
      return stage === epochData.epoch.sleep_stage
        ? 'bg-blue-500 text-white'  // Blue for epoch stage
        : 'bg-gray-200 text-gray-800 hover:bg-gray-300';
    }
    
    // If no stage is set in the epoch table
    return 'bg-gray-200 text-gray-800 hover:bg-gray-300';
  };

  const handleSleepStageClick = async (stage) => {
    try {
      const response = await fetch(`${API_URL}/api/epochs/${epochId}/manual-sleep-stage/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          sleep_stage: stage,
          staging_method: 'manual'
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update sleep stage');
      }

      const newSleepStage = await response.json();
      
      // Update epoch data with new sleep stage
      setEpochData(prev => ({
        ...prev,
        sleep_stages: prev.sleep_stages.filter(ss => ss.staging_method !== 'manual').concat(newSleepStage),
        epoch: {
          ...prev.epoch,
          sleep_stage: stage
        }
      }));
      
      // Update epochsList to reflect the new sleep stage
      setEpochsList(prevList => prevList.map(epoch => 
        epoch.id === parseInt(epochId) 
          ? { ...epoch, sleep_stage: stage }
          : epoch
      ));
    } catch (error) {
      console.error('Error updating sleep stage:', error);
      setError(error);
    }
  };

  // Freeze at mount: clearStep() in DemoWalkthrough would otherwise make re-renders return false.
  const [isDemo] = useState(() => shouldResume('epoch', recordingId));

  return (
    <div className="p-6 bg-white rounded shadow w-full mx-auto">
      <DemoWalkthrough
        enabled={isDemo}
        steps={isDemo ? [
          {
            target: '#epoch-mini-hypno',
            title: '🌙 Context: Mini Hypnogram',
            content: 'This miniature hypnogram shows where the current epoch sits in the full recording. The vertical line marks your position. Click any point to jump to that epoch.',
            placement: 'bottom',
            disableBeacon: true,
          },
          {
            target: '#epoch-pattern',
            title: '🤖 Pattern-based (ML)',
            content: 'The machine learning model outputs a probability for each sleep stage. The highest probability wins. These probabilities reflect what the model learned from large annotated datasets.',
            placement: 'bottom',
            disableBeacon: true,
          },
          {
            target: '#epoch-rulebased',
            title: '📏 Rule-based (AASM)',
            content: 'The AASM scoring rules are applied step-by-step. Each rule is shown as a line — bold means the rule matched, strikethrough means it did not. Hover the ℹ️ icon for details.',
            placement: 'bottom',
            disableBeacon: true,
          },
          {
            target: '#epoch-manual',
            title: '✏️ Manual Override',
            content: 'Disagree with the AI? Click any stage button to override the classification for this epoch. Manual overrides are tracked separately.',
            placement: 'bottom',
            disableBeacon: true,
          },
          {
            target: '#epoch-signals',
            title: '📊 Raw EEG / EOG / EMG Signals',
            content: 'Each channel is plotted as a 30-second strip. Annotations (e.g. spindles, arousals) are overlaid as coloured bands. Click a band to view details.',
            placement: 'top',
            disableBeacon: true,
          },
          {
            target: 'body',
            title: '🚀 You\'re all set!',
            content: (
              <>Use the <strong>Previous / Next</strong> buttons to step through epochs, or click the mini hypnogram to jump anywhere. Head back to the Report to see the full picture.<DontShowLink /></>
            ),
            placement: 'center',
            disableBeacon: true,
          },
        ] : []}
      />
      {/* Top Grid Section */}
      <div className="grid grid-cols-10 gap-4 m-4">
        {/* (1) Interactive Mini Hypnogram */}
        <div id="epoch-mini-hypno" className="col-span-5 bg-white rounded shadow p-2 h-32">
          {loading ? (
            <LoadingSpinner />
          ) : error ? (
            <div className="text-red-500 text-sm">Error loading hypnogram</div>
          ) : (
            <Plot
              data={[
                {
                  x: xEpochs,
                  y: yEpochs,
                  type: 'scatter',
                  mode: 'lines',
                  line: { color: 'black' },
                },
              ]}
              layout={{
                margin: { l: 40, r: 10, t: 10, b: 10 },
                xaxis: { 
                  visible: false
                },
                yaxis: { 
                  visible: true,
                  ticktext: ['N3', 'N2', 'N1', 'REM', 'Wake'],
                  tickvals: [0, 1, 2, 3, 4],
                  tickfont: { size: 10 },
                  range: [-1, 4.5],
                  fixedrange: true,
                  showgrid: false,
                  zeroline: false
                },
                shapes: [
                  verticalLine,
                  {
                    type: 'line',
                    x0: Math.min(...xEpochs),
                    x1: Math.max(...xEpochs),
                    y0: -0.5,
                    y1: -0.5,
                    line: {
                      color: 'black',
                      width: 1
                    }
                  }
                ]
              }}
              config={{ displayModeBar: false }}
              style={{ width: '100%', height: '100%' }}
              useResizeHandler={true}
              key={`mini-hypno-${windowSize.width}-${windowSize.height}`}
              onClick={(event) => {
                if (event.points && event.points.length > 0) {
                  const clickedEpochNumber = Math.round(event.points[0].x);
                  const targetEpoch = epochsList.find(
                    (e) => e.epoch_number === clickedEpochNumber
                  );
                  if (targetEpoch && targetEpoch.id !== currentEpoch) {
                    navigate(`/analysis/${recordingId}/epoch/${targetEpoch.id}`);
                  }
                }
              }}
            />
          )}
        </div>
        {/* (2) Pattern-based info */}
        <div id="epoch-pattern" className="col-span-2">
          <h2 className="font-bold text-xs">Pattern-based</h2>
          <div className="bg-white rounded shadow p-2 h-32">
            {loading ? (
              <LoadingSpinner />
            ) : error ? (
              <div className="text-red-500 text-sm">Error loading pattern data</div>
            ) : patternBased ? (
              <div className="text-xs">
                <div>Method: {patternBased.staging_method}</div>
                <div>Stage: {patternBased.sleep_stage}</div>
                <div>
                  Wake:{" "}
                  {patternBased.wake_probability !== null
                    ? `${Math.round(patternBased.wake_probability * 100)}%`
                    : 'N/A'}
                </div>
                <div>
                  N1:{" "}
                  {patternBased.n1_probability !== null
                    ? `${Math.round(patternBased.n1_probability * 100)}%`
                    : 'N/A'}
                </div>
                <div>
                  N2:{" "}
                  {patternBased.n2_probability !== null
                    ? `${Math.round(patternBased.n2_probability * 100)}%`
                    : 'N/A'}
                </div>
                <div>
                  N3:{" "}
                  {patternBased.n3_probability !== null
                    ? `${Math.round(patternBased.n3_probability * 100)}%`
                    : 'N/A'}
                </div>
                <div>
                  REM:{" "}
                  {patternBased.rem_probability !== null
                    ? `${Math.round(patternBased.rem_probability * 100)}%`
                    : 'N/A'}
                </div>
              </div>
            ) : (
              <div className="text-xs text-gray-500">No pattern-based data.</div>
            )}
          </div>
        </div>
        {/* (3) Rule-based info */}
        <div id="epoch-rulebased" className="col-span-2">
          <h2 className="font-bold text-xs">Rule-based</h2>
          <div className="bg-white rounded shadow p-2 h-32 overflow-auto">
            {loading ? (
              <LoadingSpinner />
            ) : error ? (
              <div className="text-red-500 text-sm">Error loading rule data</div>
            ) : (() => {
              if (ruleBased && ruleBased.explanation) {
                try {
                  const expl = JSON.parse(ruleBased.explanation);
                  return expl.main.map((item, idx) => {
                    const stageText = item[0].join(' ');
                    return (
                      <div key={idx} className="flex items-center space-x-1 text-xs">
                        {item[1] ? (
                          <span className="font-bold text-gray-900">{stageText}</span>
                        ) : (
                          <span className="line-through text-gray-500">{stageText}</span>
                        )}
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          className="text-blue-300 text-xs"
                          title={item[2][0]}
                        />
                      </div>
                    );
                  });
                } catch (e) {
                  return <div>Error parsing explanation</div>;
                }
              } else {
                return <div className="text-xs text-gray-500">No rule-based data.</div>;
              }
            })()}
          </div>
        </div>
        {/* (4) Sleep stage selector */}
        <div id="epoch-manual" className="col-span-1">
          <h2 className="font-bold text-xs">Manual Override</h2>
          <div className="bg-white rounded shadow p-2 h-32 flex flex-col space-y-1">
            {loading ? (
              <LoadingSpinner />
            ) : error ? (
              <div className="text-red-500 text-sm">Error loading stages</div>
            ) : (
              ['Wake', 'REM', 'N1', 'N2', 'N3'].map((stage, idx) => (
                <button
                  key={idx}
                  onClick={() => handleSleepStageClick(stage)}
                  className={`text-xs px-2 py-0.5 rounded ${getButtonStyle(stage)}`}
                >
                  {stage}
                </button>
              ))
            )}
          </div>
        </div>
      </div>
      
      {/* Epoch Signal Plots */}
      <div id="epoch-signals" className="epoch-signal-container space-y-1">
        {loading ? (
          <div className="flex items-center justify-center h-[70px] border border-gray-300 bg-gray-50 rounded transition-all duration-200">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="text-red-500 text-sm p-4 border border-red-200 bg-red-50 rounded">Error loading signals</div>
        ) : (
          epochData?.signals?.map((signal, idx) => renderEpochSignalPlot(signal, idx)) || []
        )}
      </div>
      
      {/* Navigation Buttons */}
      <div className="flex justify-between mt-4">
        <div className="flex space-x-2">
          <button
            onClick={handlePrevious}
            disabled={epochsList.findIndex(e => e.id === currentEpoch) <= 0 || loading}
            className="px-2 py-1 text-xs bg-blue-400 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            &lt; Previous
          </button>
          <button
            onClick={handleNext}
            disabled={epochsList.findIndex(e => e.id === currentEpoch) >= epochsList.length - 1 || loading}
            className="px-2 py-1 text-xs bg-blue-400 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            Next &gt;
          </button>
        </div>
        <button
          onClick={() => {
            openLevel({
              key: 'new-annotation',
              label: 'New Annotation',
              route: `/analysis/${recordingId}/epoch/${epochId}/annotation/new`
            });
          }}
          className="px-2 py-1 text-xs bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
        >
          Add Annotation
        </button>
      </div>
    </div>
  );
};

export default Epoch;
